// pdfRaster.js — DropPin's binding to the shared PDF rasteriser.
// ---------------------------------------------------------------------------
// 2026-08-12, PDF convergence Phase 2.
//
// This file used to be a VENDORED COPY of InSpec's, with a comment telling the
// next person to fix the original and re-copy. It no longer is. The rendering
// behaviour — the canvas-size budget, the page loop, memory cleanup, engine
// warming, source coercion — now comes from @insitebg/plan-markup-core/raster,
// the same package that already supplies the measurement maths.
//
// That matters most here. DropPin ships as a native Capacitor app used on site,
// often on an iPad: the shared canvas budget is what stops a large sheet
// rendering as a silent blank white rectangle, and it is no longer possible for
// that guard to be fixed in InSpec and quietly missed here.
//
// WHAT IS LEFT HERE, AND WHY
// Only the bundler-specific part: telling the shared module how to obtain
// pdf.js, where its worker asset ended up, and where the Standard 14 font data
// was emitted. `?url` is a Vite instruction rather than JavaScript, so it
// cannot be published inside a package, and where an asset lands is a bundler
// question the package must not assume.
// ---------------------------------------------------------------------------

import {
  configurePdfEngine,
  warmPdfEngine,
  renderPdfPages,
  pdfPageToPng,
  clampScale,
  MAX_CANVAS_PIXELS,
} from '@insitebg/plan-markup-core/raster';

// BOTH imports stay inside this async function. A static import of the worker
// specifier makes Vite resolve it through the pdf.js module graph, which drags
// the whole ~365 kB vendor-pdfjs chunk into first paint — verified in the built
// output. Keeping them in the async path is what makes the code-splitting work,
// and on a phone with no signal it is the difference between opening a plan and
// staring at a spinner.
// 2026-08-25 — WHERE THE STANDARD 14 OUTLINES LIVE.
//
// A PDF is not required to embed Helvetica, Times, Courier, Symbol or
// ZapfDingbats; every reader must supply them. plan-markup-core renders with
// `useSystemFonts: false` — deliberate, so a site iPad, a phone and a desktop
// draw the same marks — which leaves this URL as the ONLY source for those
// outlines. Unset, a plan renders its boxes, its rules and its logos and NOT
// ONE WORD of text, silently.
//
// Brett found this in InSpec on 2026-08-25 with a drawing that rendered in
// InSpecPDF and lost its text in the take-off. DropPin shares the rasteriser
// and had the same hole. A snag pinned to a plan whose room names and
// dimensions have vanished is a snag nobody can place.
//
// ABSOLUTE (origin-qualified) on purpose: pdf.js resolves this inside its Web
// Worker, where a relative path is wrong. Under Capacitor the origin is the
// app's own local server and the files are bundled beside the JS, so this
// resolves natively too.
//
// The files are emitted to /standard_fonts/ by the pdfjsStandardFonts plugin
// in vite.config.js. The URL and the plugin have to agree — pdfRaster.test.mjs
// checks that they do.
const BASE_URL = (import.meta.env && import.meta.env.BASE_URL) || '/';
export const STANDARD_FONT_DATA_URL =
  typeof window !== 'undefined' && window.location
    ? new URL(`${BASE_URL}standard_fonts/`, window.location.origin).href
    : `${BASE_URL}standard_fonts/`;

// ONE loader: the shared rasteriser (plan sheets) and openPdf() below (the
// client's report read in the page) get pdf.js, its worker and the Standard 14
// fonts from the same place, so they can never disagree.
const loadEngine = async () => ({
  pdfjs: await import('pdfjs-dist'),
  workerUrl: (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default,
  standardFontDataUrl: STANDARD_FONT_DATA_URL,
});
configurePdfEngine(loadEngine);

let enginePromise = null;
function engine() {
  if (!enginePromise) {
    enginePromise = loadEngine().then((e) => {
      if (e.workerUrl && e.pdfjs.GlobalWorkerOptions) e.pdfjs.GlobalWorkerOptions.workerSrc = e.workerUrl;
      return e;
    }).catch((err) => { enginePromise = null; throw err; });
  }
  return enginePromise;
}

/**
 * v0.292.0 — open a PDF from bytes for page-at-a-time rendering (the client's
 * report, read in the page). renderPdfPages() draws every page in one pass and
 * closes the document, which is right for a plan sheet and wrong for a report
 * read by scrolling, where pages render as they near the viewport and are
 * dropped again when far away. The caller owns the returned document and
 * calls destroy() when done.
 *
 * pdf.js TRANSFERS the buffer it is given to its worker (the caller's copy is
 * left detached), so the bytes are copied here: the same bytes are what the
 * Download button saves, and they have to survive being shown.
 */
export async function openPdf(bytes) {
  const { pdfjs, standardFontDataUrl } = await engine();
  const data = bytes instanceof Uint8Array ? bytes.slice() : new Uint8Array(bytes.slice(0));
  return pdfjs.getDocument({ data, standardFontDataUrl, useSystemFonts: false }).promise;
}

export { warmPdfEngine, renderPdfPages, pdfPageToPng, clampScale, MAX_CANVAS_PIXELS };
